import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getAllInvekos, proxyDownloadFile } from "services";

import { addShapeProcessor, useCancelAsyncOnUnmount } from "utils";

import { Alert, Button, Card, Icon, Spin, Table, TagNormal, Typography, Message as message } from "components";

import { ExtractionResultView } from "pages/admin/experiments/cropExtraction/components/ExtractionResultView";
import { extractCropsFromZip } from "pages/admin/experiments/cropExtraction/extractCrops";

// Per-row extraction state. Stored in a Map keyed by record id so we can show
// individual progress/result without re-fetching when a row is collapsed and
// re-expanded.
const STATUS = Object.freeze({
  idle: "idle",
  loading: "loading",
  done: "done",
  error: "error",
});

const ExtractionPanel = ({ entry }) => {
  if (!entry || entry.status === STATUS.loading) {
    return (
      <Card className="margin-top-50 margin-bottom-100" style={{ textAlign: "center", padding: "1.5rem 0" }}>
        <Spin />
        <Typography variant="paragraph-s" style={{ marginTop: "0.75rem" }}>
          Fetching and extracting crops…
        </Typography>
      </Card>
    );
  }
  if (entry.status === STATUS.error) {
    return <Alert type="red" headline="Extraction failed" description={entry.error || "Unknown error"} className="margin-top-50 margin-bottom-100" />;
  }
  if (entry.status === STATUS.done && entry.result) {
    return <ExtractionResultView result={entry.result} compact />;
  }
  return null;
};

export const AllShapefiles = () => {
  const [loading, setLoading] = useState(true);
  const [invekos, setInvekos] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  // Map<id, { status, result?, error?, controller? }>
  const [extractions, setExtractions] = useState({});

  const signal = useCancelAsyncOnUnmount()();

  useEffect(() => {
    addShapeProcessor();
  }, []);

  const loadAll = useCallback(() => {
    setLoading(true);
    getAllInvekos({ signal })
      .then((res) => setInvekos(Array.isArray(res) ? res : []))
      .catch(() => {
        message({ content: "Could not load shapefile list.", icon: "gremlin" });
      })
      .finally(() => setLoading(false));
  }, [signal]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Fetches `record.file` through the admin API proxy (avoids S3 CORS) and
  // runs extractCropsFromZip. Idempotent — already-running and already-
  // completed entries are returned without re-fetching.
  const runExtraction = useCallback(
    async (record) => {
      const { id } = record;
      const existing = extractions[id];
      if (existing && (existing.status === STATUS.loading || existing.status === STATUS.done)) return;

      setExtractions((prev) => ({ ...prev, [id]: { status: STATUS.loading } }));

      try {
        if (!record.file) throw new Error("This row has no file URL — likely an older record");
        const arrayBuffer = await proxyDownloadFile(record.file);
        if (!arrayBuffer || (arrayBuffer.byteLength !== undefined && arrayBuffer.byteLength === 0)) {
          throw new Error("Downloaded file was empty");
        }
        const blob = new Blob([arrayBuffer], { type: "application/zip" });
        const result = await extractCropsFromZip(blob);
        setExtractions((prev) => ({ ...prev, [id]: { status: STATUS.done, result } }));
      } catch (err) {
        setExtractions((prev) => ({
          ...prev,
          [id]: { status: STATUS.error, error: err?.message || "Unknown error" },
        }));
      }
    },
    [extractions]
  );

  const onExpandedRowsChange = (keys) => {
    const nextKeys = Array.from(keys);
    setExpandedRowKeys(nextKeys);
    // Lazily extract only for newly opened rows. We can't abort in-flight
    // axios requests through the project's wrapper, so collapsing a row just
    // hides the result; the eventual response is still cached.
    const newlyOpened = nextKeys.filter((k) => !expandedRowKeys.includes(k));
    for (const id of newlyOpened) {
      const record = invekos.find((r) => r.id === id);
      if (record) runExtraction(record);
    }
  };

  // Quick-look summary that renders in the row even before expansion.
  const renderSummaryTag = (id) => {
    const entry = extractions[id];
    if (!entry) return null;
    if (entry.status === STATUS.loading) return <TagNormal color="grey" text="…extracting" />;
    if (entry.status === STATUS.error) return <TagNormal color="red" text="error" />;
    if (entry.status === STATUS.done && entry.result) {
      const { result } = entry;
      if (!result.success) return <TagNormal color="red" text={result.classification.vendor} />;
      return <TagNormal color="green" text={`${result.extraction.cropCount} crops`} />;
    }
    return null;
  };

  const columns = useMemo(
    () => [
      { title: "Year", dataIndex: "year", key: "year", sorter: (a, b) => (a.year || 0) - (b.year || 0), width: 80 },
      {
        title: "Region",
        dataIndex: "region",
        key: "region",
        sorter: (a, b) => (a.region || "").localeCompare(b.region || ""),
        width: 100,
      },
      { title: "Farm", dataIndex: "farm", key: "farm", sorter: (a, b) => (a.farm || "").localeCompare(b.farm || "") },
      {
        title: "Processed",
        dataIndex: "processed",
        key: "processed",
        width: 110,
        render: (value) => (value ? <TagNormal color="green" text="Yup" /> : <TagNormal color="red" text="Nope" />),
        filters: [
          { text: "Yup", value: true },
          { text: "Nope", value: false },
        ],
        onFilter: (value, record) => record.processed === value,
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 110,
        sorter: (a, b) => (a.utc || 0) - (b.utc || 0),
        defaultSortOrder: "descend",
      },
      {
        title: "Extraction",
        key: "extractionStatus",
        width: 130,
        render: (_value, record) => renderSummaryTag(record.id),
      },
      {
        title: "",
        key: "actions",
        width: 90,
        render: (_value, record) => (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {record.farmId && (
              <Icon
                iconName="klim/farm"
                className="pointer"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  window.open(`/farm/${record.farmId}`);
                }}
              />
            )}
            {record.file && (
              <Icon
                iconName="tabler/download"
                className="pointer"
                onClick={(e) => {
                  e?.stopPropagation?.();
                  window.open(record.file);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extractions]
  );

  return (
    <div className="content">
      {!loading && invekos.length === 0 && (
        <Card>
          <Typography variant="paragraph-s">No shapefiles found.</Typography>
          <Button size="small" type="secondary" onClick={loadAll} style={{ marginTop: "0.75rem" }}>
            Reload
          </Button>
        </Card>
      )}

      {invekos.length > 0 && (
        <Card>
          <Typography variant="h4" style={{ margin: "0 0 0.5rem 0" }}>
            {invekos.length} shapefiles
          </Typography>
          <Typography variant="paragraph-s" color="grey-600" style={{ marginBottom: "1rem" }}>
            Click any row to fetch the file and run the crop extraction in your browser. Nothing is stored.
          </Typography>

          <Table
            rowKey="id"
            data={invekos}
            columns={columns}
            loading={loading}
            pagination={{ defaultPageSize: 50, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}` }}
            expandable={{
              showExpandColumn: true,
              expandRowByClick: true,
              rowExpandable: () => true,
              expandedRowKeys,
              onExpandedRowsChange,
              expandedRowRender: (record) => <ExtractionPanel entry={extractions[record.id]} />,
            }}
          />
        </Card>
      )}
    </div>
  );
};

export default AllShapefiles;
